import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import axios from 'axios';

import {
  Link,
} from 'react-router-dom';

import './PartnershipEnquiriesPage.css';

/* global process */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;

const ENQUIRIES_URL =
  `${API_BASE_URL}/api/v1/admin/partnership_enquiries`;

const STATUS_LABELS = {
  new: 'New',
  reviewed: 'Reviewed',
  contacted: 'Contacted',
  closed: 'Closed',
};

const AREA_LABELS = {
  'programme-funding':
    'Programme funding',

  technical:
    'Technical partnership',

  research:
    'Research',

  training:
    'Training',

  'private-sector':
    'Private sector',

  institutional:
    'Institutional',

  other:
    'Other',
};

const formatDate = (value) =>
  new Intl.DateTimeFormat(
    'en',
    {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }
  ).format(new Date(value));

const PartnershipEnquiriesPage = () => {
  const [enquiries, setEnquiries] =
    useState([]);

  const [status, setStatus] =
    useState('loading');

  const [error, setError] =
    useState('');

  useEffect(() => {
    const loadEnquiries = async () => {
      try {
        const response =
          await axios.get(
            ENQUIRIES_URL
          );

        setEnquiries(response.data);
        setStatus('ready');
      } catch (requestError) {
        setError(
          requestError.response?.data?.error ||
          requestError.message ||
          'Unable to load partnership enquiries.'
        );

        setStatus('failed');
      }
    };

    loadEnquiries();
  }, []);

  const counts = useMemo(
    () => ({
      total: enquiries.length,

      new:
        enquiries.filter(
          (item) =>
            item.status === 'new'
        ).length,

      reviewed:
        enquiries.filter(
          (item) =>
            item.status === 'reviewed'
        ).length,

      contacted:
        enquiries.filter(
          (item) =>
            item.status === 'contacted'
        ).length,
    }),
    [enquiries]
  );

  if (status === 'loading') {
    return (
      <main className="admin-page">
        <p>
          Loading partnership enquiries...
        </p>
      </main>
    );
  }

  return (
    <main className="admin-page">
      <header className="admin-page-header">
        <div>
          <p className="admin-eyebrow">
            Partnership management
          </p>

          <h1>
            Partnership Enquiries
          </h1>

          <p>
            Review and follow up on partnership
            requests submitted through the ACE
            public website.
          </p>
        </div>
      </header>

      {error && (
        <div className="pe-message error">
          {error}
        </div>
      )}

      <section className="pe-summary">
        <article>
          <span>Total enquiries</span>
          <strong>{counts.total}</strong>
        </article>

        <article>
          <span>New</span>
          <strong>{counts.new}</strong>
        </article>

        <article>
          <span>Reviewed</span>
          <strong>{counts.reviewed}</strong>
        </article>

        <article>
          <span>Contacted</span>
          <strong>{counts.contacted}</strong>
        </article>
      </section>

      <section className="pe-list">
        {enquiries.map((enquiry) => (
          <article
            key={enquiry.id}
            className="pe-row"
          >
            <div className="pe-status-column">
              <span
                className={
                  `pe-status ${enquiry.status}`
                }
              >
                {
                  STATUS_LABELS[
                    enquiry.status
                  ] || enquiry.status
                }
              </span>

              <small>
                {formatDate(
                  enquiry.created_at
                )}
              </small>
            </div>

            <div className="pe-content">
              <p className="pe-area">
                {
                  AREA_LABELS[
                    enquiry.partnership_area
                  ] ||
                  enquiry.partnership_area
                }
              </p>

              <h2>
                {enquiry.subject}
              </h2>

              <p className="pe-person">
                <strong>
                  {enquiry.name}
                </strong>

                {' · '}

                {enquiry.organisation}

                {enquiry.country
                  ? ` · ${enquiry.country}`
                  : ''}
              </p>

              <p className="pe-preview">
                {enquiry.message.slice(
                  0,
                  220
                )}

                {enquiry.message.length >
                220
                  ? '...'
                  : ''}
              </p>
            </div>

            <div className="pe-action">
              <Link
                to={
                  `/admin/partnership-enquiries/` +
                  `${enquiry.id}`
                }
              >
                Open enquiry
              </Link>
            </div>
          </article>
        ))}

        {enquiries.length === 0 && (
          <div className="pe-empty">
            <h2>
              No partnership enquiries
            </h2>

            <p>
              New submissions from the public
              partnership form will appear here.
            </p>
          </div>
        )}
      </section>
    </main>
  );
};

export default PartnershipEnquiriesPage;
